import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';

type TUseNotificationTimer = {
    isMeetingStarted: boolean;
    isMeetingFinished: boolean;
    time: string;
};
interface IUseNotificationTimer {
    startTime: string;
    endTime: string;
}

export const useNotificationTimer = ({ startTime, endTime }: IUseNotificationTimer): TUseNotificationTimer => {
    const [meetingTimeOffset, setMeetingTimeOffset] = useState(0);
    const [isMeetingStarted, setIsMeetingStarted] = useState(false);
    const [isMeetingFinished, setIsMeetingFinished] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current); // очистка интервала в начале
        }

        const lessonStartTime = dayjs(startTime);
        const lessonEndTime = dayjs(endTime);

        const updateTimer = () => {
            const currentTime = dayjs();
            const timeBeforeStart = lessonStartTime.diff(currentTime, 'milliseconds');
            const timeAfterStart = currentTime.diff(lessonStartTime, 'milliseconds');

            // нужно округлять вручную в большую сторону, так как dayjs секунды округляет в меньшую,
            // что приводит к появлению двух нулей подряд в момент lessonStartTime
            const roundedTimeBeforeStart = Math.ceil(timeBeforeStart / 1000);
            const roundedTimeAfterStart = Math.floor(timeAfterStart / 1000);

            if (roundedTimeBeforeStart > 0) {
                // Отображаем время до начала встречи
                setMeetingTimeOffset(roundedTimeBeforeStart);
                setIsMeetingStarted(false);
            } else if (roundedTimeBeforeStart <= 0 && !isMeetingStarted) {
                // Когда встреча начинается, сразу же начинаем отсчёт с 0
                setIsMeetingStarted(true);
                setMeetingTimeOffset(roundedTimeAfterStart);
            } else if (isMeetingStarted && roundedTimeAfterStart >= 0) {
                // Отсчёт времени от начала встречи
                setMeetingTimeOffset(roundedTimeAfterStart);
            }

            // После окончания, останавливаем таймер
            if (currentTime.isAfter(lessonEndTime)) {
                setIsMeetingFinished(true);
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
            }
        };

        updateTimer();
        timerRef.current = setInterval(updateTimer, 1000); // обновление каждую секунду

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current); // очистка интервала при размонтировании
            }
        };
    }, [startTime, endTime]);

    const formatTime = (totalSeconds: number) => {
        if (totalSeconds >= 0) {
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;

            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }

        return `--:--`;
    };

    return {
        isMeetingStarted,
        isMeetingFinished,
        time: formatTime(meetingTimeOffset),
    };
};
